import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { FileSignature, Search, CheckCircle, Clock, Gift, XCircle, RotateCcw, ExternalLink, BadgeCheck, RefreshCw, CalendarClock, Euro, Ban, Plus, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { usePagamentiPendenti } from '../contexts/PagamentiPendentiContext';
import { avvisoDurataMinima } from '../lib/durataMinima';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '-');
const oggi = () => new Date().toISOString().slice(0, 10);

function statoPagamento(c) {
  if (c.disdetta) return 'chiuso';
  if (c.data_inizio_fatturazione && c.data_inizio_fatturazione.slice(0, 10) > oggi()) return 'omaggio';
  if (c.pagato_corrente === false) return 'da_pagare';
  return 'pagato';
}

function BadgeStato({ c, onToggle }) {
  const stato = statoPagamento(c);
  if (stato === 'chiuso') {
    return (
      <Badge variant="outline" className="rounded-none text-[10px] font-mono text-muted-foreground border-muted-foreground" data-testid={`ctr-badge-chiuso-${c.id}`}>
        <XCircle className="h-3 w-3 mr-1" /> CHIUSO
      </Badge>
    );
  }
  if (stato === 'omaggio') {
    return (
      <Badge variant="outline" className="rounded-none text-[10px] font-mono text-sky-600 border-sky-500 bg-sky-50 dark:bg-sky-950/30" title={`Periodo omaggio: la fatturazione parte il ${fmtDate(c.data_inizio_fatturazione)}`} data-testid={`ctr-badge-omaggio-${c.id}`}>
        <Gift className="h-3 w-3 mr-1" /> OMAGGIO fino al {fmtDate(c.data_inizio_fatturazione)}
      </Badge>
    );
  }
  if (stato === 'da_pagare') {
    return (
      <Badge variant="outline" className="rounded-none text-[10px] font-mono text-red-600 border-red-500 bg-red-50 dark:bg-red-950/30 cursor-pointer hover:bg-red-100" title="DA PAGARE — un clic per segnare PAGATO" onClick={() => onToggle(c, true)} data-testid={`ctr-badge-dapagare-${c.id}`}>
        <Clock className="h-3 w-3 mr-1" /> DA PAGARE
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="rounded-none text-[10px] font-mono text-emerald-600 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 cursor-pointer hover:bg-emerald-100" title={`Pagato${c.data_ultimo_pagamento ? ' il ' + fmtDate(c.data_ultimo_pagamento) : ''} — clic per riportare a DA PAGARE`} onClick={() => onToggle(c, false)} data-testid={`ctr-badge-pagato-${c.id}`}>
      <CheckCircle className="h-3 w-3 mr-1" /> PAGATO
    </Badge>
  );
}

export default function ContrattiPage() {
  const navigate = useNavigate();
  const { refresh: refreshPendenti } = usePagamentiPendenti();
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scadDa, setScadDa] = useState('');
  const [scadA, setScadA] = useState('');
  const [mostraChiusi, setMostraChiusi] = useState(false);
  const [filtroKpi, setFiltroKpi] = useState(null);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [clienti, setClienti] = useState([]);
  const [clienteSearch, setClienteSearch] = useState('');
  const [selezionati, setSelezionati] = useState(new Set());

  const fetchContratti = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contratti`);
      setContratti(res.data || []);
    } catch (e) {
      toast.error('Errore caricamento contratti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContratti(); }, [fetchContratti]);

  // Dashboard riepilogativa (ex Rinnovi Abbonamenti)
  const oggiStr = oggi();
  const limite60 = new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);
  const attivi = contratti.filter((c) => !c.disdetta);
  const scaduti = attivi.filter((c) => (c.data_fine || '').slice(0, 10) < oggiStr);
  const inScadenza = attivi.filter((c) => { const f = (c.data_fine || '').slice(0, 10); return f >= oggiStr && f <= limite60; });
  const conDisdetta = contratti.filter((c) => c.disdetta);
  const totaleCanoni = attivi.reduce((sum, c) => sum + (c.canone_annuo || 0), 0);

  const matchKpi = (c) => {
    const f = (c.data_fine || '').slice(0, 10);
    switch (filtroKpi) {
      case 'da_rinnovare': return !c.disdetta && f && f <= limite60;
      case 'scaduti': return !c.disdetta && f && f < oggiStr;
      case 'in_scadenza': return !c.disdetta && f >= oggiStr && f <= limite60;
      case 'disdetta': return !!c.disdetta;
      case 'attivi': return !c.disdetta;
      default: return true;
    }
  };

  const filtrati = contratti.filter((c) => {
    if (!matchKpi(c)) return false;
    if (filtroKpi !== 'disdetta' && !mostraChiusi && c.disdetta) return false;
    const s = search.toLowerCase();
    const matchSearch = !s || (c.cliente_nome || '').toLowerCase().includes(s) || `${c.anno}/${String(c.numero).padStart(3, '0')}`.includes(s);
    const fine = (c.data_fine || '').slice(0, 10);
    const matchDa = !scadDa || (fine && fine >= scadDa);
    const matchA = !scadA || (fine && fine <= scadA);
    return matchSearch && matchDa && matchA;
  });

  const daPagareFiltrati = filtrati.filter((c) => statoPagamento(c) === 'da_pagare');

  const togglePagamento = async (c, pagato) => {
    try {
      const res = await axios.post(`${API}/contratti/${c.id}/pagamento`, { pagato });
      toast.success(res.data.messaggio);
      fetchContratti();
      refreshPendenti();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const segnaTuttiPagati = async () => {
    const ids = selezionati.size > 0 ? [...selezionati] : daPagareFiltrati.map((c) => c.id);
    if (ids.length === 0) return toast.info('Nessun contratto da pagare');
    try {
      const res = await axios.post(`${API}/contratti/segna-pagati`, { ids });
      toast.success(res.data.messaggio);
      setSelezionati(new Set());
      fetchContratti();
      refreshPendenti();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const chiudi = async (c) => {
    const riapri = !!c.disdetta;
    const avviso = !riapri ? avvisoDurataMinima(c) : null;
    const msg = riapri
      ? `Riaprire il contratto ${c.anno}/${String(c.numero).padStart(3, '0')} di ${c.cliente_nome}?`
      : `Chiudere il contratto ${c.anno}/${String(c.numero).padStart(3, '0')} di ${c.cliente_nome}?${avviso ? `\n\n${avviso}` : ''}\n\nNessuna fattura verrà più generata automaticamente.${avviso ? ' La decisione finale resta all\'operatore.' : ''}`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${API}/contratti/${c.id}/chiudi`, riapri ? { riapri: true } : {});
      toast.success(res.data.messaggio);
      fetchContratti();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const toggleSel = (id) => {
    setSelezionati((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelAll = () => {
    if (selezionati.size === daPagareFiltrati.length && daPagareFiltrati.length > 0) setSelezionati(new Set());
    else setSelezionati(new Set(daPagareFiltrati.map((c) => c.id)));
  };

  const apriNuovo = async () => {
    setNuovoOpen(true);
    if (clienti.length === 0) {
      try {
        const res = await axios.get(`${API}/clienti`);
        setClienti(res.data || []);
      } catch (e) { toast.error('Errore caricamento clienti'); }
    }
  };

  const stampaModelloVuoto = async () => {
    try {
      const res = await axios.get(`${API}/contratti/modello-vuoto/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contratto_modello_vuoto.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Modello vuoto scaricato: pronto da stampare e compilare a mano');
    } catch (e) {
      toast.error('Errore generazione modello vuoto');
    }
  };

  const clientiFiltrati = clienti.filter((c) => {
    const s = clienteSearch.toLowerCase();
    return !s || (c.ragione_sociale || '').toLowerCase().includes(s) || (c.codice_cliente || '').toLowerCase().includes(s) || (c.partita_iva || '').includes(s);
  }).slice(0, 30);

  return (
    <div className="space-y-4" data-testid="contratti-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight flex items-center gap-2">
            <FileSignature className="h-6 w-6" /> Contratti di Assistenza
          </h1>
          <p className="text-xs text-muted-foreground font-mono">{filtrati.length} contratti · {daPagareFiltrati.length} da pagare</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-none font-mono uppercase text-xs" onClick={apriNuovo} data-testid="nuovo-contratto-btn">
            <Plus className="h-4 w-4 mr-1" /> Nuovo Contratto
          </Button>
          <Button variant="outline" className="rounded-none font-mono uppercase text-xs" onClick={stampaModelloVuoto} data-testid="stampa-modello-vuoto-btn" title="PDF in bianco da stampare e compilare a mano dal cliente">
            <Printer className="h-4 w-4 mr-1" /> Stampa modello vuoto
          </Button>
          <Button className="rounded-none font-mono uppercase text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={segnaTuttiPagati} disabled={daPagareFiltrati.length === 0} data-testid="segna-tutti-pagati-btn">
            <BadgeCheck className="h-4 w-4 mr-1" />
            Segna tutti pagati {selezionati.size > 0 ? `(${selezionati.size} selezionati)` : daPagareFiltrati.length > 0 ? `(${daPagareFiltrati.length})` : ''}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="contratti-dashboard">
        {[
          { key: 'da_rinnovare', label: 'Da rinnovare', value: scaduti.length + inScadenza.length, icon: RefreshCw, cls: 'text-cyan-600 border-cyan-500/40 bg-cyan-500/5', testid: 'kpi-da-rinnovare' },
          { key: 'scaduti', label: 'Scaduti', value: scaduti.length, icon: Clock, cls: 'text-red-600 border-red-500/40 bg-red-500/5', testid: 'kpi-scaduti' },
          { key: 'in_scadenza', label: 'In Scadenza (60gg)', value: inScadenza.length, icon: CalendarClock, cls: 'text-amber-600 border-amber-500/40 bg-amber-500/5', testid: 'kpi-in-scadenza' },
          { key: 'disdetta', label: 'Con Disdetta', value: conDisdetta.length, icon: Ban, cls: 'text-slate-500 border-slate-400/40 bg-slate-500/5', testid: 'kpi-disdette' },
          { key: 'attivi', label: 'Totale canoni', value: `€ ${totaleCanoni.toFixed(0)}`, icon: Euro, cls: 'text-emerald-600 border-emerald-500/40 bg-emerald-500/5', testid: 'kpi-totale-canoni' },
        ].map((k) => {
          const Icon = k.icon;
          const attivo = filtroKpi === k.key;
          return (
            <div
              key={k.label}
              className={`border-l-4 border border-border p-3 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${k.cls} ${attivo ? 'ring-2 ring-primary shadow-md' : ''}`}
              onClick={() => setFiltroKpi(attivo ? null : k.key)}
              title={attivo ? 'Clicca per rimuovere il filtro' : 'Clicca per filtrare la lista'}
              data-testid={k.testid}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {k.label}</div>
              <p className="text-2xl font-black tabular-nums mt-1">{k.value}</p>
            </div>
          );
        })}
      </div>
      {filtroKpi && (
        <div className="flex items-center gap-2 text-xs font-mono" data-testid="filtro-kpi-attivo">
          <Badge className="rounded-none bg-primary text-primary-foreground uppercase">
            Filtro: {{ da_rinnovare: 'Da rinnovare', scaduti: 'Scaduti', in_scadenza: 'In scadenza 60gg', disdetta: 'Con disdetta', attivi: 'Tutti gli attivi' }[filtroKpi]}
          </Badge>
          <button className="underline text-muted-foreground" onClick={() => setFiltroKpi(null)} data-testid="rimuovi-filtro-kpi">rimuovi</button>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cerca cliente o numero..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 rounded-none" data-testid="contratti-search" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">Scadenza dal</Label>
          <Input type="date" value={scadDa} onChange={(e) => setScadDa(e.target.value)} className="rounded-none" data-testid="contratti-scad-da" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">al</Label>
          <Input type="date" value={scadA} onChange={(e) => setScadA(e.target.value)} className="rounded-none" data-testid="contratti-scad-a" />
        </div>
        <label className="flex items-center gap-2 pb-2 cursor-pointer select-none" data-testid="mostra-chiusi-toggle">
          <Checkbox checked={mostraChiusi} onCheckedChange={(v) => setMostraChiusi(!!v)} data-testid="mostra-chiusi-checkbox" />
          <span className="text-xs font-mono uppercase text-muted-foreground">Mostra chiusi ({conDisdetta.length})</span>
        </label>
        {(scadDa || scadA) && (
          <Button variant="ghost" size="sm" className="rounded-none text-xs" onClick={() => { setScadDa(''); setScadA(''); }} data-testid="contratti-reset-filtri">
            Azzera filtri
          </Button>
        )}
      </div>

      <Dialog open={nuovoOpen} onOpenChange={setNuovoOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="nuovo-contratto-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono uppercase text-sm flex items-center gap-2"><FileSignature className="h-4 w-4" /> Nuovo Contratto — scegli il cliente</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input autoFocus placeholder="Cerca per nome, codice o P.IVA..." value={clienteSearch} onChange={(e) => setClienteSearch(e.target.value)} className="pl-8 rounded-none" data-testid="nuovo-contratto-cliente-search" />
          </div>
          <div className="max-h-72 overflow-y-auto border border-border divide-y divide-border">
            {clientiFiltrati.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3">Nessun cliente trovato</p>
            ) : clientiFiltrati.map((cl) => (
              <button
                key={cl.id}
                className="w-full text-left p-2.5 hover:bg-accent transition-colors flex items-center justify-between gap-2"
                onClick={() => { setNuovoOpen(false); navigate(`/contratti/nuovo?cliente_id=${cl.id}`); }}
                data-testid={`nuovo-contratto-cliente-${cl.id}`}
              >
                <span className="text-sm font-bold">{cl.ragione_sociale}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{cl.codice_cliente || cl.partita_iva || ''}</span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Il contratto si apre già precompilato con impianti, abbonamento e punto vendita del cliente.</p>
        </DialogContent>
      </Dialog>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Caricamento...</p>
      ) : filtrati.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Nessun contratto trovato</p>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-8">
                  <Checkbox checked={daPagareFiltrati.length > 0 && selezionati.size === daPagareFiltrati.length} onCheckedChange={toggleSelAll} data-testid="sel-all-contratti" title="Seleziona tutti i DA PAGARE" />
                </th>
                <th>N.</th>
                <th>Cliente</th>
                <th>Scadenza</th>
                <th>Canone</th>
                <th>Pagamento canone</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtrati.map((c) => {
                const stato = statoPagamento(c);
                return (
                  <tr key={c.id} className={stato === 'da_pagare' ? 'bg-red-50/50 dark:bg-red-950/10' : c.disdetta ? 'opacity-60' : ''}>
                    <td>
                      {stato === 'da_pagare' && (
                        <Checkbox checked={selezionati.has(c.id)} onCheckedChange={() => toggleSel(c.id)} data-testid={`sel-contratto-${c.id}`} />
                      )}
                    </td>
                    <td className="font-mono text-xs">{c.anno}/{String(c.numero).padStart(3, '0')}</td>
                    <td className="font-bold text-sm cursor-pointer hover:underline" onClick={() => navigate(`/clienti/${c.cliente_id}`)} data-testid={`ctr-cliente-${c.id}`}>{c.cliente_nome || '-'}</td>
                    <td className="font-mono text-xs">{fmtDate((c.data_fine || '').slice(0, 10))}</td>
                    <td className="font-mono text-xs">{(c.canone_annuo || 0).toFixed(2)} €</td>
                    <td><BadgeStato c={c} onToggle={togglePagamento} /></td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" onClick={() => navigate(`/contratti/${c.id}`)} title="Apri contratto" data-testid={`open-ctr-${c.id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        {c.disdetta ? (
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-sky-600" onClick={() => chiudi(c)} title="Riapri contratto" data-testid={`riapri-ctr-${c.id}`}>
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => chiudi(c)} title="Chiudi contratto (blocca rinnovi e fatturazione automatica)" data-testid={`chiudi-ctr-${c.id}`}>
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
